/**
 * The ITP register, its items, its review exchange and the planner's candidates.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Every function states the shape it was promised, field by field. A response the server
 * did not build is an error here and never a screen of undefined. Nothing in this module
 * decides what a principal may see: it asks and renders the answer, including the answer
 * being an empty register or a 404 that means either "no such plan" or "not yours".
 */

import { parseCitations, type Citation } from "./standards";
import { apiJson, bool, listBody, num, optStr, query, str, type Problem } from "./client";

export interface ItpSummary {
  id: string;
  humanId: string;
  title: string;
  revision: number;
  workflowState: string;
  reviewCycles: number;
  contractId: string;
  contractHumanId: string;
  contractorPartyName: string;
  disciplineCode: string;
  disciplineLabel: string;
  submittedAt: string | null;
  approvedAt: string | null;
  itemCount: number;
}

export interface ItpItem {
  id: string;
  itemNo: string;
  sequence: number;
  activityDescription: string;
  inspectionMethod: string;
  acceptanceCriteria: string;
  referenceDocument: string | null;
  verifyingDocument: string | null;
  status: string;
  statusLabel: string;
  isAccepted: boolean;
  isWithdrawn: boolean;
  becomesCandidate: boolean;
  /** Wave R0: the item's remarks, sent and read back with its other content. */
  remarks: string | null;
  /** Wave R0: who intervenes on this item and at which level, the operator's first. */
  interventions: ItemIntervention[];
  /**
   * Wave INST ruling 2: the item is the template, its requests are instances. One chip
   * per instance the item knows, as the server reads it (ItemInstance).
   */
  instances: ItemInstance[];
  /** The contractor's declaration that the item's scope is complete, while it stands. */
  declaredInstanceCount: number | null;
  declaredCompleteAt: string | null;
  /** The operator's concurrence: only then is the item CLEARED. */
  completionConcurredAt: string | null;
  /**
   * Review ruling A: whether THIS caller's declaration or concurrence would be taken now,
   * read by the server through the acts' own predicates. The line offers each act only
   * when its flag is true; the server's 404 and 409 still decide.
   */
  mayDeclareComplete: boolean;
  mayConcurComplete: boolean;
  /** Wave STD ruling 12: the clauses this item cites (ItemSummary.citations). */
  citations: Citation[];
}

/** The states an instance chip reads (ItemInstance.state). */
export type InstanceState = "CLEARED" | "WAIVED" | "REQUESTED" | "OPEN";

/** One instance of an item (wave INST ruling 2). */
export interface ItemInstance {
  /** Null for a request raised before instance labels: the whole item. */
  label: string | null;
  state: InstanceState;
  /** The settling record, else the newest request naming the instance. */
  verificationId: string | null;
  verificationHumanId: string | null;
  settledAt: string | null;
}

/** One intervention row on an item, as the register returns it (InterventionOut). */
export interface ItemIntervention {
  partyTypeId: string;
  partyTypeCode: string;
  partyTypeLabel: string;
  isInternal: boolean;
  code: string;
  label: string;
  notation: string;
  requiresOperatorAttendance: boolean;
}

export interface ReviewEntry {
  id: string;
  move: string;
  visibility: string;
  body: string | null;
  itpItemId: string | null;
  itemNo: string | null;
  inReplyToId: string | null;
  at: string;
  actorDisplayName: string;
  actingPartyName: string;
}

export interface ItpTransitionEntry {
  id: string;
  event: string;
  fromState: string;
  toState: string;
  comment: string | null;
  at: string;
  actorDisplayName: string | null;
}

export interface Candidate {
  id: string;
  itpItemId: string;
  interventionCode: string;
  interventionLabel: string;
  notation: string;
  itemNo: string;
  activityDescription: string;
  inspectionMethod: string;
  acceptanceCriteria: string;
  itpId: string;
  itpHumanId: string;
  contractId: string;
  contractHumanId: string;
  contractorPartyId: string;
  contractorPartyName: string;
  disciplineCode: string;
  disciplineLabel: string;
  /** Wave W2: born at the item's acceptance, so the plan may still be in review. */
  itpWorkflowState: string;
  createdAt: string;
  plannedActivityId: string | null;
  /** The contractor's standing RFI on this item, when it requested the date itself. */
  requestedRfiId: string | null;
  /** Whether the caller may plan this candidate onto a schedule (the PLAN verb). */
  mayPlan: boolean;
}

function summary(body: Record<string, unknown>): ItpSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    title: str(body.title),
    revision: num(body.revision),
    workflowState: str(body.workflow_state),
    reviewCycles: num(body.review_cycles),
    contractId: str(body.contract_id),
    contractHumanId: str(body.contract_human_id),
    contractorPartyName: str(body.contractor_party_name),
    disciplineCode: str(body.discipline_code),
    disciplineLabel: str(body.discipline_label),
    submittedAt: optStr(body.submitted_at),
    approvedAt: optStr(body.approved_at),
    itemCount: num(body.item_count),
  };
}

export async function listItps(
  token: string,
  filters: {
    contractId?: string | null;
    workflowState?: string | null;
    /** Wave W1: the ITP approvals queue, only plans in review the caller may act on. */
    pendingForMe?: boolean;
    signal?: AbortSignal;
  },
): Promise<{ total: number; items: ItpSummary[] }> {
  const body = await apiJson(
    token,
    `/itps${query({
      contract_id: filters.contractId,
      workflow_state: filters.workflowState,
      pending_for_me: filters.pendingForMe === true ? true : undefined,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(summary) };
}

export async function getItp(
  token: string,
  itpId: string,
  signal?: AbortSignal,
): Promise<ItpSummary> {
  return apiJson(token, `/itps/${itpId}`, (raw) => summary(assertRecord(raw)), { signal });
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function intervention(body: Record<string, unknown>): ItemIntervention & { itemId: string } {
  return {
    itemId: str(body.itp_item_id),
    partyTypeId: str(body.intervening_party_type_id),
    partyTypeCode: str(body.party_type_code),
    partyTypeLabel: str(body.party_type_label),
    isInternal: bool(body.is_internal),
    code: str(body.intervention_code),
    label: str(body.intervention_label),
    notation: str(body.notation),
    requiresOperatorAttendance: bool(body.requires_operator_attendance),
  };
}

/**
 * The register's flat intervention list, hung on each item (operator rows first). The
 * server sends the list beside the items to avoid a lookup per item (ItemList); a body
 * without it (an older stub) reads as no interventions rather than as an error.
 */
function withInterventions(items: ItpItem[], raw: unknown): ItpItem[] {
  if (raw === undefined) {
    return items;
  }
  if (!Array.isArray(raw)) {
    throw new Error("Expected an intervention list");
  }
  const byItem = new Map<string, ItemIntervention[]>();
  for (const row of raw) {
    const { itemId, ...one } = intervention(assertRecord(row));
    byItem.set(itemId, [...(byItem.get(itemId) ?? []), one]);
  }
  return items.map((entry) => ({
    ...entry,
    interventions: [...(byItem.get(entry.id) ?? [])].sort(
      (a, b) => Number(b.isInternal) - Number(a.isInternal),
    ),
  }));
}

function item(body: Record<string, unknown>): ItpItem {
  return {
    id: str(body.id),
    itemNo: str(body.item_no),
    sequence: num(body.sequence),
    activityDescription: str(body.activity_description),
    inspectionMethod: str(body.inspection_method),
    acceptanceCriteria: str(body.acceptance_criteria),
    referenceDocument: optStr(body.reference_document),
    verifyingDocument: optStr(body.verifying_document),
    status: str(body.status),
    statusLabel: str(body.status_label),
    isAccepted: bool(body.is_accepted),
    isWithdrawn: bool(body.is_withdrawn),
    becomesCandidate: bool(body.becomes_a_planning_candidate),
    remarks: optStr(body.remarks),
    interventions: [],
    instances: (Array.isArray(body.instances) ? body.instances : []).map((raw) =>
      instance(assertRecord(raw)),
    ),
    declaredInstanceCount:
      typeof body.declared_instance_count === "number" ? body.declared_instance_count : null,
    declaredCompleteAt: optStr(body.declared_complete_at),
    completionConcurredAt: optStr(body.completion_concurred_at),
    mayDeclareComplete: body.may_declare_complete === true,
    mayConcurComplete: body.may_concur_complete === true,
    citations: parseCitations(body.citations),
  };
}

const INSTANCE_STATES: readonly InstanceState[] = ["CLEARED", "WAIVED", "REQUESTED", "OPEN"];

function instance(body: Record<string, unknown>): ItemInstance {
  return {
    label: optStr(body.instance_label),
    // A state this client does not know reads as OPEN: still owed, never shown settled.
    state: INSTANCE_STATES.find((one) => one === body.state) ?? "OPEN",
    verificationId: optStr(body.verification_id),
    verificationHumanId: optStr(body.verification_human_id),
    settledAt: optStr(body.settled_at),
  };
}

/**
 * What a contractor principal supplies to register a plan (ItpCreate). The author's own
 * party is read server-side from the token; the client never sends a party id.
 */
export interface ItpCreatePayload {
  contractId: string;
  disciplineId: string;
  title: string;
}

export async function createItp(token: string, payload: ItpCreatePayload): Promise<ItpSummary> {
  return apiJson(token, `/itps`, (raw) => summary(assertRecord(raw)), {
    method: "POST",
    body: {
      contract_id: payload.contractId,
      discipline_id: payload.disciplineId,
      title: payload.title,
    },
  });
}

/**
 * One item's content, as the backend's ItemCreate / ItemPatch demand it (wave R0: the
 * whole row, the interventions included). The form keeps text; the wire is typed.
 */
export interface ItemPayload {
  itemNo: string;
  activityDescription: string;
  inspectionMethod: string;
  acceptanceCriteria: string;
  referenceDocument: string;
  verifyingDocument: string;
  remarks: string;
  /** The item's order in the plan, as typed; blank lets the server append it. */
  sequence: string;
  /** The level each party type holds on the item, by party type id; blank is none. */
  interventions: Readonly<Record<string, string>>;
}

/** An empty item draft, its sequence defaulting to the next place in the plan. */
export function emptyItemPayload(nextSequence: number): ItemPayload {
  return {
    itemNo: "",
    activityDescription: "",
    inspectionMethod: "",
    acceptanceCriteria: "",
    referenceDocument: "",
    verifyingDocument: "",
    remarks: "",
    sequence: String(nextSequence),
    interventions: {},
  };
}

/** An existing item, as its edit form opens. */
export function itemPayloadOf(entry: ItpItem): ItemPayload {
  return {
    itemNo: entry.itemNo,
    activityDescription: entry.activityDescription,
    inspectionMethod: entry.inspectionMethod,
    acceptanceCriteria: entry.acceptanceCriteria,
    referenceDocument: entry.referenceDocument ?? "",
    verifyingDocument: entry.verifyingDocument ?? "",
    remarks: entry.remarks ?? "",
    sequence: String(entry.sequence),
    interventions: Object.fromEntries(
      entry.interventions.map((one) => [one.partyTypeId, one.code]),
    ),
  };
}

/** Each item field's name on the wire: what a server refusal names (wave W1.5). */
export const ITEM_WIRE_FIELDS: Record<keyof ItemPayload, string> = {
  itemNo: "item_no",
  activityDescription: "activity_description",
  inspectionMethod: "inspection_method",
  acceptanceCriteria: "acceptance_criteria",
  referenceDocument: "reference_document",
  verifyingDocument: "verifying_document",
  remarks: "remarks",
  sequence: "sequence",
  interventions: "interventions",
};

/** One intervention as InterventionIn takes it. */
export interface InterventionWire {
  intervening_party_type_id: string;
  intervention_code: string;
}

/** The wire body of ItemCreate and ItemPatch (both take the same fields). */
export interface ItemWire {
  item_no: string;
  activity_description: string;
  inspection_method: string;
  acceptance_criteria: string;
  reference_document: string | null;
  verifying_document: string | null;
  remarks: string | null;
  sequence: number | null;
  interventions: InterventionWire[];
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The body for create AND edit. `partyOrder` puts the rows in the editor's order (the
 * operator first) so a refusal naming `interventions.0` names the row the user sees
 * first. A PATCH replaces the item's interventions with exactly these rows.
 */
export function itemWire(payload: ItemPayload, partyOrder: readonly string[] = []): ItemWire {
  const sequence = Number.parseInt(payload.sequence.trim(), 10);
  const rank = (id: string): number => {
    const at = partyOrder.indexOf(id);
    return at < 0 ? partyOrder.length : at;
  };
  return {
    item_no: payload.itemNo,
    activity_description: payload.activityDescription,
    inspection_method: payload.inspectionMethod,
    acceptance_criteria: payload.acceptanceCriteria,
    reference_document: blankToNull(payload.referenceDocument),
    verifying_document: blankToNull(payload.verifyingDocument),
    remarks: blankToNull(payload.remarks),
    sequence: Number.isFinite(sequence) && sequence >= 1 ? sequence : null,
    interventions: Object.entries(payload.interventions)
      .filter(([, code]) => code !== "")
      .sort(([a], [b]) => rank(a) - rank(b))
      .map(([partyTypeId, code]) => ({
        intervening_party_type_id: partyTypeId,
        intervention_code: code,
      })),
  };
}

function itemList(body: unknown): { total: number; items: ItpItem[] } {
  const record = assertRecord(body);
  if (!Array.isArray(record.items)) {
    throw new Error("Expected an item list");
  }
  return {
    total: num(record.total),
    items: withInterventions(
      record.items.map((row) => item(assertRecord(row))),
      record.interventions,
    ),
  };
}

/** Register one item. The server allows it only while the plan is a DRAFT. */
export async function createItem(
  token: string,
  itpId: string,
  payload: ItemPayload,
  partyOrder: readonly string[] = [],
): Promise<{ total: number; items: ItpItem[] }> {
  return apiJson(token, `/itps/${itpId}/items`, itemList, {
    method: "POST",
    body: itemWire(payload, partyOrder),
  });
}

/** Rewrite one item's content. The server allows it only while the plan is a DRAFT. */
export async function editItem(
  token: string,
  itpId: string,
  itemId: string,
  payload: ItemPayload,
  partyOrder: readonly string[] = [],
): Promise<{ total: number; items: ItpItem[] }> {
  return apiJson(token, `/itps/${itpId}/items/${itemId}`, itemList, {
    method: "PATCH",
    body: itemWire(payload, partyOrder),
  });
}

/** Remove one item. The server allows it only while the plan is a DRAFT. */
export async function deleteItem(token: string, itpId: string, itemId: string): Promise<boolean> {
  return apiJson(
    token,
    `/itps/${itpId}/items/${itemId}`,
    (raw) => bool(assertRecord(raw).deleted),
    { method: "DELETE" },
  );
}

export async function listItems(
  token: string,
  itpId: string,
  signal?: AbortSignal,
): Promise<{ total: number; items: ItpItem[] }> {
  return apiJson(token, `/itps/${itpId}/items`, itemList, { signal });
}

/**
 * Wave INST ruling 2: the main contractor declares the item's scope complete, with how
 * many instances it has. The server answers the plan's items again (ItemList).
 */
export async function declareItemComplete(
  token: string,
  itpId: string,
  itemId: string,
  declaredInstanceCount: number,
): Promise<{ total: number; items: ItpItem[] }> {
  return apiJson(token, `/itps/${itpId}/items/${itemId}/declare-complete`, itemList, {
    method: "POST",
    body: { declared_instance_count: declaredInstanceCount },
  });
}

/** Wave INST ruling 2: an operator holding ITP_ITEM ACCEPT concurs; the item is CLEARED. */
export async function concurItemComplete(
  token: string,
  itpId: string,
  itemId: string,
): Promise<{ total: number; items: ItpItem[] }> {
  return apiJson(token, `/itps/${itpId}/items/${itemId}/concur-complete`, itemList, {
    method: "POST",
  });
}

function review(body: Record<string, unknown>): ReviewEntry {
  return {
    id: str(body.id),
    move: str(body.move),
    visibility: str(body.visibility),
    body: optStr(body.body),
    itpItemId: optStr(body.itp_item_id),
    itemNo: optStr(body.item_no),
    inReplyToId: optStr(body.in_reply_to_id),
    at: str(body.at),
    actorDisplayName: str(body.actor_display_name),
    actingPartyName: str(body.acting_party_name),
  };
}

export async function listReview(
  token: string,
  itpId: string,
  signal?: AbortSignal,
): Promise<ReviewEntry[]> {
  const body = await apiJson(
    token,
    `/itps/${itpId}/review`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of review entries");
      }
      return raw.map((entry) => review(assertRecord(entry)));
    },
    { signal },
  );
  return body;
}

function transitionEntry(body: Record<string, unknown>): ItpTransitionEntry {
  return {
    id: str(body.id),
    event: str(body.event),
    fromState: str(body.from_state),
    toState: str(body.to_state),
    comment: optStr(body.comment),
    at: str(body.at),
    actorDisplayName: optStr(body.actor_display_name),
  };
}

export async function listItpTransitions(
  token: string,
  itpId: string,
  signal?: AbortSignal,
): Promise<ItpTransitionEntry[]> {
  return apiJson(
    token,
    `/itps/${itpId}/transitions`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of transitions");
      }
      return raw.map((entry) => transitionEntry(assertRecord(entry)));
    },
    { signal },
  );
}

export interface ItpTransitionOutcome {
  event: string;
  fromState: string;
  toState: string;
  candidatesCreated: number;
}

/** One guarded state change on the plan. The server refuses what the policy refuses. */
export async function transitionItp(
  token: string,
  itpId: string,
  event: string,
  payload: { comment?: string | null } = {},
): Promise<ItpTransitionOutcome> {
  return apiJson(
    token,
    `/itps/${itpId}/transitions/${event}`,
    (raw) => {
      const body = assertRecord(raw);
      return {
        event: str(body.event),
        fromState: str(body.from_state),
        toState: str(body.to_state),
        candidatesCreated: num(body.candidates_created ?? 0),
      };
    },
    { method: "POST", body: payload },
  );
}

export interface ReviewMovePayload {
  move: "COMMENT" | "CONSULT" | "ACCEPT" | "OBJECT";
  body?: string | null;
  itpItemId?: string | null;
  inReplyToId?: string | null;
  internal?: boolean;
}

export interface ReviewMoveOutcome {
  id: string;
  move: string;
  itemStatus: string | null;
  /** Wave W2: OPENED or WITHDRAWN when the move changed the planner's to-do list. */
  candidate: string | null;
}

export async function recordReviewMove(
  token: string,
  itpId: string,
  payload: ReviewMovePayload,
): Promise<ReviewMoveOutcome> {
  return apiJson(
    token,
    `/itps/${itpId}/review`,
    (raw) => {
      const body = assertRecord(raw);
      // The move's identifier is what the attachment picker links its file to
      // (M5 P5g: the itemized loop's per-move attachments).
      return {
        id: str(body.id),
        move: str(body.move),
        itemStatus: optStr(body.item_status),
        candidate: optStr(body.candidate),
      };
    },
    {
      method: "POST",
      // The wire is snake_case; the item address and the reply target are what
      // make a move an itemized one.
      body: {
        move: payload.move,
        body: payload.body ?? null,
        itp_item_id: payload.itpItemId ?? null,
        in_reply_to_id: payload.inReplyToId ?? null,
        internal: payload.internal ?? false,
      },
    },
  );
}

function candidate(body: Record<string, unknown>): Candidate {
  return {
    id: str(body.id),
    itpItemId: str(body.itp_item_id),
    interventionCode: str(body.intervention_code),
    interventionLabel: str(body.intervention_label),
    notation: str(body.notation),
    itemNo: str(body.item_no),
    activityDescription: str(body.activity_description),
    inspectionMethod: str(body.inspection_method),
    acceptanceCriteria: str(body.acceptance_criteria),
    itpId: str(body.itp_id),
    itpHumanId: str(body.itp_human_id),
    contractId: str(body.contract_id),
    contractHumanId: str(body.contract_human_id),
    contractorPartyId: str(body.contractor_party_id),
    contractorPartyName: str(body.contractor_party_name),
    disciplineCode: str(body.discipline_code),
    disciplineLabel: optStr(body.discipline_label) ?? str(body.discipline_code),
    itpWorkflowState: optStr(body.itp_workflow_state) ?? "APPROVED",
    createdAt: optStr(body.created_at) ?? "",
    plannedActivityId: optStr(body.planned_activity_id),
    requestedRfiId: optStr(body.requested_rfi_id),
    mayPlan: body.may_plan === true,
  };
}

export async function listCandidates(
  token: string,
  filters: { unplannedOnly?: boolean; contractId?: string | null; signal?: AbortSignal } = {},
): Promise<{ total: number; items: Candidate[] }> {
  const body = await apiJson(
    token,
    `/planning-candidates${query({
      unplanned_only: filters.unplannedOnly,
      contract_id: filters.contractId,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(candidate) };
}

/** The refusal document shape, re-exported so screens need one import for it. */
export type { Problem };
